<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/production/productivity_by_producer_exp/show_process.yml"
  lang="yaml"
></i18n>
<template>
  <go-back-link :text="$t('labels.go_back_link')" :name="routeName('list_processes')" :params="{
    organization_id: organization_id
  }" />

  <template v-if="load_general_data">
    <bread-crumbs :labels="[module_name, harvest_process.name]" />

    <div class="mt-8 flex flex-row justify-between">
      <badge-default :label="`${$t('labels.producers')}: ${total_producers}`" />

      <button type="button" @click.prevent="downloadRepProducersXlsx()" class="btn-white">
        <i :class="[
          'fa-solid',
          downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
        ]"></i>
        <span class="space-icon"></span>{{ $t("labels.download_button") }}
      </button>
    </div>

    <div class="mt-6 flex flex-col">
      <ag-grid-vue v-if="producers.length > 0" style="width: 100%; height: 700px" class="ag-theme-alpine w-full h-full"
        :columnDefs="cols" :defaultColDef="defaultColDef" :rowData="producers" @grid-ready="onGridReady">
      </ag-grid-vue>
    </div>
    <div class="flex flex-row justify-between">
      <div class="mt-4">
        <div class="rounded-2xl bg-blue-50 p-4">
          <div class="flex">
            <span><i class="fa-solid fa-circle-info text-blue-400" /></span>
            <div class="flex items-start justify-start mx-3">
              <div>
                <h3 class="text-medium-sm text-blue-800">
                  {{ $t("labels.performance_labels") }}
                </h3>
                <div class="text-light-sm text-blue-700">
                  <ul role="list" class="list-disc pl-5 space-y-1">
                    <li>
                      <b>{{ $t("labels.total_exported") }}</b>: {{ $t("labels.total_exported_explanation") }}
                    </li>
                    <li>
                      <b> {{ $t("labels.productivity_monthly") }} </b>: {{ $t("labels.productivity_monthly_explanation")
                      }}
                    </li>
                    <li>
                      <b>{{ $t("labels.productivity_weekly") }}</b>: {{ $t("labels.productivity_weekly_explanation") }}
                    </li>
                    <li>
                      <b>{{ $t("labels.productivity") }}</b>: {{ $t("labels.productivity_explanation") }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <table-paginator v-model="current_page" :total_rows="total_producers"
        :rows_per_page="rows_per_page"></table-paginator>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";

import Requester from "@/views/organization/requesters/production/productivity_by_producer_exp/show_process.js";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";

export default {
  name: "ORGProductionProductivityByProducerExpShowProcess",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      load_general_data: false,
      downloading: false,
      current_page: 1,
      total_producers: 0,
      available_pages: 1,
      rows_per_page: 12,
      harvest_process: {},
      producers: [],
      package_headers: [],
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "org_producer_code",
          filter: true,
          cellRendererSelector: params => this.goToShowProducer(params)
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer_code",
          filter: true,
          cellRendererSelector: params => this.goToShowProducer(params)
        },
        {
          headerName: this.$t("model.producer.lastname"),
          field: "lastname",
          filter: true
        },
        {
          headerName: this.$t("model.producer.name"),
          field: "name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.hectares"),
          field: "hectares",
          filter: "agNumberColumnFilter",
          width: 150,
          cellRenderer: params => this.numberWithPrecision(params.value, 2)
        },
        {
          headerName: this.$t("labels.total_exported"),
          headerComponent: {
            template: `<p title="${this.$t("labels.total_exported_explanation")}">${this.$t("labels.total_exported")}</p>`
          },
          field: "total_exported",
          filter: "agNumberColumnFilter",
          cellRenderer: params => this.numberWithPrecision(params.value, 2),
        },
        {
          headerName: this.$t("labels.productivity_monthly"),
          headerComponent: {
            template: `<p title="${this.$t("labels.productivity_monthly_explanation")}">${this.$t("labels.productivity_monthly")}</p>`
          },
          field: "productivity_monthly",
          filter: "agNumberColumnFilter",
          cellRenderer: params => this.numberWithPrecision(params.value, 2)
        },
        {
          headerName: this.$t("labels.productivity_weekly"),
          headerComponent: {
            template: `<p title="${this.$t("labels.productivity_weekly_explanation")}">${this.$t("labels.productivity_weekly")}</p>`
          },
          field: "productivity_weekly",
          filter: "agNumberColumnFilter",
          cellRenderer: params => this.numberWithPrecision(params.value, 2)
        },
        {
          headerName: this.$t("labels.productivity"),
          headerComponent: {
            template: `<p title="${this.$t("labels.productivity_explanation")}">${this.$t("labels.productivity")}</p>`
          },
          field: "productivity",
          filter: "agNumberColumnFilter",
          cellRenderer: params => this.numberWithPrecision(params.value, 2)
        }
      ]
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true }
  },
  computed: {
    cols: function () {
      let package_headers = [];
      this.package_headers.forEach(package_header => {
        package_headers.push({
          headerName: `${this.$t('model.harvested_batch.num_packages')} (${package_header['label']})`,
          resizable: true,
          field: `packages.${package_header['key']}`
        });
      });
      return [
        ...this.columnDefs,
        ...package_headers
      ]
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchTotalProducers();
      await this.fetchProducers();
      this.load_general_data = true;
    },
    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchTotalProducers: async function () {
      let response = await Requester.fetchTotalProducers(this.organization_id, this.harvest_process_id);
      this.total_producers = response.data;
      this.available_pages = Array.from(
        {
          length: Math.ceil(this.total_producers / this.rows_per_page)
        },
        (_, i) => i + 1
      );
    },
    fetchProducers: async function () {
      let response = await Requester.fetchProducers(
        this.organization_id,
        this.harvest_process_id,
        this.current_page - 1,
        this.rows_per_page
      );
      this.producers = response.data.producers;
      if (this.package_headers.length == 0) this.package_headers = response.data.package_headers;
    },
    downloadRepProducersXlsx: async function () {
      this.downloading = true;
      try {
        var response = await Requester.downloadRepProducersXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        this.downloadFile(
          file,
          `Rendimiento - ${this.harvest_process.name}.xlsx`
        );
      } catch (error) {
        error;
      }
      this.downloading = false;
    },
    goToShowProducer: function (params) {
      {
        return {
          component: GoToLink,
          params: {
            text: params.value,
            path_name: this.routeName("show_producer"),
            params_path: {
              organization_id: this.organization_id,
              harvest_process_id: this.harvest_process_id,
              producer_id: params.data.id
            }
          }
        };
      }
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 150
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  },
  watch: {
    current_page: function () {
      this.fetchProducers();
    }
  },
};
</script>
